import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { Save } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useForm } from '@tanstack/react-form';
import { QueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { omit } from 'ramda';
import { FC, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { DialogTitleWithCloseIcon } from 'src/components/organisms/DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';
import { useCompositePartner } from 'src/hooks/partner/useCompositePartner';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import {
    CreateSalesCampaignInput,
    StorefrontTypeModel,
    UpdateSalesCampaignInput,
} from 'src/infrastructure/rest-api/api-types';

import { useApiMutation } from 'src/infrastructure/rest-api/useApi';

type FormValue = CreateSalesCampaignInput & UpdateSalesCampaignInput;

interface SalesCampaignDialogProps extends Omit<DialogProps, 'defaultValue' | 'onClose'> {
    defaultValue: FormValue;
    isEditing: boolean;
    onClose?: (ok: boolean) => void;
    storefrontTypes: StorefrontTypeModel[];
}

export const SalesCampaignDialog: FC<SalesCampaignDialogProps> = ({
    defaultValue,
    storefrontTypes,
    isEditing,
    open,
    onClose,
}) => {
    const compositePartner = useCompositePartner();

    const invalidateStyleTimelineQueries = (queryClient: QueryClient) => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey[0];
                return typeof key === 'string' && key.startsWith('/api/styletimeline/partners/');
            },
        });
    };

    const [createSalesCampaign] = useApiMutation('/api/salescampaigns', 'post', {
        update(data, _variables, queryClient) {
            const path = { partnerId: compositePartner?.id };
            queryClient.setQueryData(['/api/salescampaigns/partners/{partnerId}', path], data);
            invalidateStyleTimelineQueries(queryClient);
        },
    });
    const [updateSalesCampaign] = useApiMutation('/api/salescampaigns', 'put', {
        update(data, _variables, queryClient) {
            const path = { partnerId: compositePartner?.id };
            queryClient.setQueryData(['/api/salescampaigns/partners/{partnerId}', path], data);
            invalidateStyleTimelineQueries(queryClient);
        },
    });
    const showSnackbar = useChangesSnackbar();
    const form = useForm({
        defaultValues: defaultValue,
        onSubmit: async ({ value }) => {
            try {
                if (!isEditing) {
                    await createSalesCampaign({
                        body: omit(['salesCampaignId'], value),
                    });
                } else {
                    await updateSalesCampaign({
                        body: omit(['partnerCompositeId', 'storefrontTypeId'], value),
                    });
                }

                showSnackbar();
                if (typeof onClose === 'function') {
                    onClose(true);
                }
            } catch (error: unknown) {
                showSnackbar(error);
            }
        },
    });

    useKeyboardShortcut({
        shortcutName: 'CLEAR',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            form.reset();
        },
        capture: true,
    });

    useKeyboardShortcut({
        shortcutName: 'SAVE',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
        },
        capture: true,
    });

    const handleClose = useCallback(() => {
        if (typeof onClose === 'function') {
            onClose(false);
        }
    }, [onClose]);

    return (
        <Dialog open={open} onClose={handleClose} data-testid="salesCampaignsDialog">
            <form
                style={{ paddingTop: '0.5rem' }}
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
                noValidate
            >
                <DialogTitleWithCloseIcon
                    title={isEditing ? 'Edit campaign' : 'Add campaign'}
                    handleClose={handleClose}
                />
                <DialogContent sx={{ width: '400px', maxWidth: '100%', boxSizing: 'border-box' }}>
                    <form.Subscribe selector={(state) => state.isSubmitting}>
                        {(isSubmitting) => (
                            <Stack
                                sx={{
                                    gap: '0.75rem',
                                }}
                            >
                                <form.Field
                                    name="name"
                                    validators={{
                                        onChange: ({ value }) => {
                                            if (!value?.trim()) {
                                                return 'Campaign name is required';
                                            }
                                        },
                                    }}
                                >
                                    {(field) => (
                                        <TextField
                                            autoFocus
                                            required
                                            label="Campaign name"
                                            value={field.state.value}
                                            onBlur={field.handleBlur}
                                            onChange={(e) => field.handleChange(e.target.value)}
                                            placeholder="Enter campaign name"
                                            disabled={isSubmitting}
                                        />
                                    )}
                                </form.Field>
                                <form.Field
                                    name="startDate"
                                    validators={{
                                        onChange: ({ value }) => {
                                            if (!value) {
                                                return 'Start date is required';
                                            }
                                        },
                                    }}
                                >
                                    {(field) => (
                                        <DatePicker
                                            selected={field.state.value ? new Date(field.state.value) : null}
                                            onBlur={field.handleBlur}
                                            onChange={(newDate: Date | null) => {
                                                return newDate ? field.handleChange(format(newDate, 'yyyy-MM-dd')) : '';
                                            }}
                                            onChangeRaw={(event) => event?.preventDefault()}
                                            portalId="dialog"
                                            dateFormat={'dd/MM-yyyy'}
                                            placeholderText="Select start date"
                                            disabled={isSubmitting}
                                            showMonthDropdown
                                            showYearDropdown
                                            customInput={<TextField required fullWidth label="Start date *" />}
                                        />
                                    )}
                                </form.Field>
                                <form.Field
                                    name="endDate"
                                    validators={{
                                        onChange: ({ value }) => {
                                            if (!value) {
                                                return 'End date is required';
                                            }
                                        },
                                    }}
                                >
                                    {(field) => (
                                        <form.Subscribe selector={(state) => state.values.startDate}>
                                            {(startDate) => (
                                                <DatePicker
                                                    selected={field.state.value ? new Date(field.state.value) : null}
                                                    onBlur={field.handleBlur}
                                                    onChange={(newDate: Date | null) =>
                                                        newDate ? field.handleChange(format(newDate, 'yyyy-MM-dd')) : ''
                                                    }
                                                    onChangeRaw={(event) => event?.preventDefault()}
                                                    minDate={startDate ? new Date(startDate) : undefined}
                                                    portalId="dialog"
                                                    dateFormat={'dd/MM-yyyy'}
                                                    placeholderText="Select end date"
                                                    disabled={isSubmitting}
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    customInput={<TextField required fullWidth label="End date *" />}
                                                />
                                            )}
                                        </form.Subscribe>
                                    )}
                                </form.Field>
                                <form.Field
                                    name="storefrontTypeId"
                                    validators={{
                                        onChange: ({ value }) => {
                                            if (!value) {
                                                return 'Storefront type is required';
                                            }
                                        },
                                    }}
                                >
                                    {(field) => (
                                        <FormControl fullWidth>
                                            <InputLabel id="storefront-type-label">Storefront type *</InputLabel>
                                            <Select
                                                labelId="storefront-type-label"
                                                id="storefront-type-select"
                                                label="Storefront type *"
                                                value={field.state.value ? field.state.value?.toString() : ''}
                                                onBlur={field.handleBlur}
                                                onChange={(event) => field.handleChange(Number(event.target.value))}
                                                renderValue={(value) => {
                                                    if (value === '') {
                                                        return <em>Select storefront type</em>;
                                                    }
                                                    return storefrontTypes.find(
                                                        (storefrontType) => storefrontType.id === Number(value)
                                                    )?.name;
                                                }}
                                                disabled={isSubmitting || isEditing}
                                            >
                                                <MenuItem disabled value={''}>
                                                    Select storefront type
                                                </MenuItem>
                                                {storefrontTypes.map((storefrontType) => (
                                                    <MenuItem key={storefrontType.id} value={storefrontType.id}>
                                                        {storefrontType.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </form.Field>
                            </Stack>
                        )}
                    </form.Subscribe>
                </DialogContent>
                <DialogActions>
                    <form.Subscribe
                        selector={(state) => [
                            state.canSubmit &&
                                !!state.values.name?.trim() &&
                                !!state.values.startDate &&
                                !!state.values.endDate &&
                                !!state.values.storefrontTypeId,
                            state.isSubmitting,
                        ]}
                    >
                        {([canSubmit, isSubmitting]) => (
                            <>
                                <Button
                                    variant="text"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        if (typeof onClose === 'function') {
                                            onClose(false);
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    loading={isSubmitting}
                                    loadingPosition="start"
                                    startIcon={<Save />}
                                    variant="contained"
                                    color="info"
                                    type="submit"
                                    disabled={!canSubmit}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </form.Subscribe>
                </DialogActions>
            </form>
        </Dialog>
    );
};
